'use client';

import React, { useEffect, useState } from 'react';
import {
  LoggedOutLibraryData,
  LoggedOutProductLibraryContainsItem,
  SelectedLibraryItem,
} from '../../../types/library';
import LibraryWrapperLoggedOut from '../../../components/library/LibraryWrapperLoggedOut';
import {
  getInitialItem,
  getMatchingItems,
  getLibrarySubset,
} from '../../../app/_lib/shared/library/logged-out/format';
import { SCROLL_TOP_CLASSNAME } from '../../../constants/library';
import { useHash } from '../../../hooks/useHash';

type Props = {
  libraryData: LoggedOutLibraryData;
  showFullLibrary?: boolean;
};

/**
 * Determines the open index based on the breadcrumb.
 * @param {string} breadcrumb - The breadcrumb string.
 * @returns {number} The open index.
 */
export const determineOpenIndex = (breadcrumb: string) => {
  switch (breadcrumb) {
    case 'organ-systems':
    case 'usmle-step-2-review':
      return 1;
    case 'usmle-step-1-review':
    case 'featured-series':
      return 2;
    case 'core-competencies':
      return 3;
    default:
      return 0;
  }
};

/**
 * Gets the target ID based on the length of breadcrumb parts.
 * @param {string[]} breadcrumbParts - The breadcrumb parts.
 * @returns {string} The target ID.
 */
export const getTargetId = (breadcrumbParts: string[]) => {
  switch (breadcrumbParts.length) {
    case 2:
      return `${breadcrumbParts[0]}-${breadcrumbParts[1]}-L2`;
    case 3:
      return `${breadcrumbParts[0]}-${breadcrumbParts[1]}-${breadcrumbParts[2]}-L3`;
    case 4:
      return `${breadcrumbParts[0]}-${breadcrumbParts[1]}-${breadcrumbParts[2]}-${breadcrumbParts[3]}-L4`;
    case 5:
      return `${breadcrumbParts[0]}-${breadcrumbParts[1]}-${breadcrumbParts[2]}-${breadcrumbParts[3]}-${breadcrumbParts[4]}-L5`;
    default:
      return null;
  }
};

/**
 * Scrolls to the specified element.
 * @param {string} targetId - The ID of the target element.
 * @param {number} timeoutDuration - The duration of the timeout.
 */
export const scrollToElement = (targetId: string, timeoutDuration: number) => {
  setTimeout(() => {
    const element = document.getElementById(targetId);
    if (element) {
      // Calculate the position to scroll to with padding
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - 100;

      // Scroll to the calculated position
      elementPosition &&
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
    }
  }, timeoutDuration);
};

/**
 * LibraryLoggedOut.
 */
const LibraryLoggedOut = (props: Props) => {
  const { libraryData, showFullLibrary = false } = props;
  const [fullLibrary] = libraryData.fullLibrary;
  // Get the breadcrumb parts from the URL hash
  const breadcrumbParts = useHash().replace('#', '').split('/');
  const hashItems = getMatchingItems(
    fullLibrary,
    breadcrumbParts[0],
    breadcrumbParts[1],
  );
  const [selectedItem, setSelectedItem] = useState<
    SelectedLibraryItem | undefined
  >(getInitialItem(fullLibrary));
  const [openIndex, setOpenIndex] = useState<number>(0);
  const targetId = getTargetId(breadcrumbParts);

  useEffect(() => {
    if (hashItems) {
      setSelectedItem(hashItems);
      setOpenIndex(determineOpenIndex(breadcrumbParts[0]));
    }

    const scrollL2 = document.getElementById(
      `${breadcrumbParts[0]}-${breadcrumbParts[1]}`,
    );
    if (scrollL2) {
      setTimeout(() => {
        scrollL2.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  const handleSelect = (
    l1: LoggedOutProductLibraryContainsItem,
    l2: LoggedOutProductLibraryContainsItem,
  ) => {
    setSelectedItem({ l1, l2 });

    if (showFullLibrary) {
      // If whole library is visible, scroll to L2
      window.location.href = `#${l1.id}_${l2.id}`;
    } else {
      // If whole library is not visible, scroll to top
      const scrollTopElement = document.querySelector(
        `.${SCROLL_TOP_CLASSNAME}`,
      );
      if (scrollTopElement) {
        scrollTopElement.scrollIntoView();
      }
    }
  };

  const library = showFullLibrary
    ? fullLibrary
    : getLibrarySubset(fullLibrary, selectedItem);

  return (
    <LibraryWrapperLoggedOut
      libraryData={libraryData}
      library={library}
      onSelect={handleSelect}
      selectedItem={selectedItem}
      openIndex={openIndex}
      {...(targetId && { targetId })}
    />
  );
};

export default LibraryLoggedOut;
